import React from "react";
import { Box, Typography, Button, Container, Chip, Avatar, AvatarGroup, Grid,  } from "@mui/material";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import routes from "../../../Config/routes";
import { BentoGrid, BentoGridItem } from "../../../Components/BentoGrid";
import MagicCard from "../../../Components/MagicCard";
import { useTheme } from '@mui/material/styles';
const CollaborateSection = () => {
  const theme = useTheme();
  const ref = useRef(null);
  const navigate = useNavigate();
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.6
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const blitzModules = [
    {
      title: "Campaigns",
      description: "Create and manage end-to-end campaigns with budgets, timelines, and deliverables",
      icon: "🚀",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      users: 4,
      colSpan: 2
    },
    {
      title: "Marketplace",
      description: "Browse, filter, and shortlist creators; view rich profiles and broadcast briefs",
      icon: "🛍️",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      users: 3,
      colSpan: 1
    },
    {
      title: "Partnerships",
      description: "Promote top creators to Partner status with one-click management",
      icon: "🤝",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      users: 2,
      colSpan: 1
    },
    {
      title: "Chats",
      description: "In-app negotiation with deliverable checklists and approval workflows",
      icon: "💬",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      users: 5,
      colSpan: 1
    },
    {
      title: "BlitzPay",
      description: "Instant payouts, advance management, and seamless transaction reconciliation",
      icon: "💳",
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      users: 3,
      colSpan: 1
    }
  ];

  const stats = [
    { value: "1000+", label: "Campaigns", color: "#667eea" },
    { value: "1200+", label: "Creators", color: "#f5576c" },
    { value: "98%", label: "Success Rate", color: "#00f2fe" },
    { value: "48h", label: "Response Time", color: "#feca57" }
  ];

  const creatorProfiles = [
    { name: "Alex", color: "#667eea" },
    { name: "Blake", color: "#764ba2" },
    { name: "Charlie", color: "#f093fb" },
    { name: "Dana", color: "#f5576c" },
    { name: "Eli", color: "#4facfe" },
    { name: "Fran", color: "#00f2fe" },
    { name: "Gray", color: "#43e97b" },
    { name: "Harper", color: "#38f9d7" }
  ];

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        py: { xs: 6, md: 8 },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)"
        }
      }}
    >
      {/* Background orbs */}
      <Box sx={{
        position: "absolute",
        top: "20%",
        left: "10%",
        width: { xs: "300px", md: "500px" },
        height: { xs: "300px", md: "500px" },
        background: "radial-gradient(circle, rgba(67,233,123,0.1) 0%, transparent 70%)",
        filter: "blur(70px)",
        animation: "float 18s ease-in-out infinite"
      }} />
      <Box sx={{
        position: "absolute",
        bottom: "10%",
        right: "10%",
        width: { xs: "400px", md: "600px" },
        height: { xs: "400px", md: "600px" },
        background: "radial-gradient(circle, rgba(79,172,254,0.12) 0%, transparent 70%)",
        filter: "blur(80px)",
        animation: "float 20s ease-in-out infinite reverse"
      }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <motion.div variants={itemVariants}>
              <Chip 
                label="COLLABORATION" 
                sx={{ 
                  mb: 3,
                  background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  px: 2,
                  py: 0.5,
                  height: "auto"
                }} 
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  color: "#0f172a",
                  mb: 3,
                  letterSpacing: "-0.02em",
                  fontFamily:"Moret Variable" ,fontWeight:'600'
                }}
              >
                BLITZ COLLABORATE
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography
                variant="h6"
                sx={{
                  color: "#475569",
                  mb: 4,
                  maxWidth: "800px",
                  mx: "auto",
                  lineHeight: 1.7,
                  fontSize: { xs: "1.125rem", md: "1.375rem" },
                  fontWeight: 300
                }}
              >
              Empower creator collaborations. Discover talent, manage campaigns, and streamline workflows in one platform
              </Typography>
            </motion.div>

            {/* Stats Grid */}
            <motion.div variants={itemVariants}>
              <Grid container spacing={3} sx={{ mb: 4, justifyContent: "center" }}>
                {stats.map((stat, index) => (
                  <Grid item xs={6} md={3} key={stat.label}>
                    <MagicCard
                      gradientFrom={stat.color}
                      gradientTo={stat.color}
                      gradientColor={stat.color}
                      gradientOpacity={0.06}
                      gradientSize={200}
                      sx={{
                        p: { xs: 3, md: 4 },
                        border: "1px solid rgba(0,0,0,0.04)",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#0f172a",
                          mb: 0.5,
                          fontSize: { xs: "2rem", md: "2.5rem" },
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#64748b",
                          fontWeight: 400,
                          fontSize: "0.8rem"
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </MagicCard>
                  </Grid>
                ))}
              </Grid>
            </motion.div>

            {/* Creator Profiles */}
            {/* <motion.div variants={itemVariants}>
              <Box sx={{ mb: 5, display: "flex", justifyContent: "center" }}>
                <AvatarGroup 
                  max={8}
                  sx={{
                    '& .MuiAvatar-root': {
                      width: 56,
                      height: 56,
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      border: '3px solid rgba(0,0,0,0.5)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.2) translateY(-6px)',
                        zIndex: 10,
                        boxShadow: '0 12px 24px rgba(0,0,0,0.4)'
                      }
                    }
                  }}
                >
                  {creatorProfiles.map((creator, index) => (
                    <motion.div
                      key={creator.name}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={isInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
                      transition={{ 
                        delay: index * 0.08,
                        duration: 0.5,
                        type: "spring"
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          bgcolor: creator.color,
                          cursor: 'pointer'
                        }}
                      >
                        {creator.name[0]}
                      </Avatar>
                    </motion.div>
                  ))}
                </AvatarGroup>
              </Box>
            </motion.div> */}
          </Box>

          {/* Modules Bento Grid */}
          <Box sx={{ mb: 5 }}>
            <BentoGrid>
              {blitzModules.map((module, index) => (
                <BentoGridItem
                  key={module.title}
                  title={module.title}
                  description={module.description}
                  colSpan={module.colSpan}
                  gradient={module.gradient}
                  icon={
                    <Typography sx={{ fontSize: "1.5rem" }}>
                      {module.icon}
                    </Typography>
                  }
                  header={
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                      }}
                    >
                      <Typography sx={{ fontSize: "3rem" }}>
                        {module.icon}
                      </Typography>
                      <Box
                        sx={{
                          px: 2,
                          py: 0.5,
                          borderRadius: "20px",
                          background: module.gradient,
                          color: "white",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                        }}
                      >
                        {module.users} Active
                      </Box>
                    </Box>
                  }
                />
              ))}
            </BentoGrid>
          </Box>

          {/* CTA */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(routes.onboard)}
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  px: { xs: 5, md: 8 },
                  py: { xs: 2, md: 2.5 },
                  borderRadius: "50px",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                  position: "relative",
                  overflow: "hidden",
                  textTransform: "none",
                  '&::before': {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    transition: "left 0.5s",
                  },
                  '&:hover': {
                    bgcolor: theme.palette.primary.light,
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: '0 16px 56px rgba(0,0,0,0.2)',
                    '&::before': {
                      left: '100%'
                    }
                  },
                  transition: "all 0.3s ease"
                }}
              >
                <span style={{ position: "relative", zIndex: 1 }}>
                  Start Collaborating Now
                </span>
              </Button>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default CollaborateSection;
